// pages/InstallPage.jsx — extracted from App.js (behaviour-preserving split).
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  ChefHat,
  ClipboardList,
  Download,
  RefreshCw,
  Store,
  Wine,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getStoredDeviceRole, INSTALLABLE_ROLES, isInstalledAppEnvironment, setStoredBusinessCode, setStoredDeviceRole } from "@/lib/device";
import { detectInstallPlatform, isInstallPromptAvailable, triggerInstallPrompt } from "@/lib/pwa";


// ─────────────────────────────────────────────────────────────────────
// /install — friendly install landing page
// ─────────────────────────────────────────────────────────────────────
// Admins copy a per-role link like `/install?role=staff&business=spur`
// and send it to the restaurant. Opening the link:
//   1) Stamps `business_code` + `device_role` into localStorage
//      immediately, so even before the actual install, the device is
//      pre-configured. After install, the PWA launches → DeviceRoot
//      auto-redirects to the right login.
//   2) Surfaces platform-appropriate install UI:
//        • Android / Desktop Chromium → big "Install Now" button that
//          calls the captured beforeinstallprompt.
//        • iOS Safari → Share → Add to Home Screen visual instructions.
//        • Already installed → auto-redirects to the role's login.
export function InstallPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawRole = (searchParams.get("role") || "").toLowerCase();
  const businessCode = (searchParams.get("business") || "").trim().toLowerCase();
  const installToken = (searchParams.get("token") || "").trim();
  const rolesForPicker = ["owner", "staff", "kitchen", "bar"]; // admin has its own install path

  // Session 27.60 — When we're ALREADY running inside a native shell
  // (Capacitor APK or Tauri EXE), the "install this as a PWA" wizard
  // is meaningless — the user just installed the APK. The right flow
  // is: pick role → go to that role's login screen → login itself
  // downloads all business data. Skip the "setup progress" wizard
  // entirely unless there's an install token (admin-issued pre-auth
  // flow where business data can be downloaded without login).
  //
  // v1.1.1 also aggressively redirected to /install/setup — that was
  // too eager. It bypassed role selection AND left the setup page
  // trying to connect with no business context, showing "Setup
  // couldn't finish". Now we only auto-forward to setup when a
  // token IS present.
  useEffect(() => {
    let inNative = false;
    try { inNative = isInstalledAppEnvironment(); } catch (_) { inNative = false; }
    if (!inNative) return;
    // Session 30.11 — the native app no longer uses this wizard as its
    // "choose dashboard" page; send it to the boot flow instead.
    if (!installToken) { navigate("/", { replace: true }); return; }
    const qs = searchParams.toString();
    const target = `/install/setup${qs ? `?${qs}` : ""}`;
    navigate(target, { replace: true });
  }, [navigate, searchParams, installToken]);

  // Session 25.7 — Pre-auth install token flow. When the URL carries a
  // signed install token, the tablet can download every business
  // resource WITHOUT any user login. We route straight to the setup
  // progress page which knows how to consume the token.
  useEffect(() => {
    if (installToken) {
      const next = encodeURIComponent(rawRole ? `/${rawRole}` : "/");
      navigate(`/install/setup?token=${encodeURIComponent(installToken)}&next=${next}`, { replace: true });
    }
  }, [installToken, rawRole, navigate]);

  const [pickedRole, setPickedRole] = useState(() => {
    // Priority: ?role= URL param > any role already saved for this device
    if (INSTALLABLE_ROLES.includes(rawRole)) return rawRole;
    try {
      const saved = getStoredDeviceRole();
      if (rolesForPicker.includes(saved)) return saved;
    } catch (_) { /* noop */ }
    return "";
  });
  const role = pickedRole; // convenience alias for the rest of the JSX

  // Session 27.61 — In Capacitor/Tauri, as soon as the user picks a
  // role we skip the entire "install this as a PWA" section and jump
  // straight to that role's login screen. The APK is already
  // installed — there's nothing to prompt for. Business data will be
  // downloaded by the login flow itself.
  useEffect(() => {
    if (!role) return;
    let inNative = false;
    try { inNative = isInstalledAppEnvironment(); } catch (_) { inNative = false; }
    if (!inNative) return;
    // Persist the role choice so a subsequent app relaunch lands the
    // user directly on the same login screen instead of showing the
    // picker again.
    try { setStoredDeviceRole(role); } catch (_) { /* noop */ }
    navigate(`/${role}`, { replace: true });
  }, [role, navigate]);

  const [platform, setPlatform] = useState(() => detectInstallPlatform());
  const [installAvailable, setInstallAvailable] = useState(() => isInstallPromptAvailable());
  const [installState, setInstallState] = useState("idle"); // idle | prompting | accepted | dismissed | error

  // Pre-stamp device config so the FIRST app-launch lands on the right
  // login. We do this whenever the role/business becomes known — either
  // from the URL, or after the user taps a role tile.
  useEffect(() => {
    try {
      if (businessCode) setStoredBusinessCode(businessCode);
      if (role) setStoredDeviceRole(role);
    } catch (_) { /* noop */ }
  }, [businessCode, role]);

  // Listen for the install-prompt becoming available after page load.
  useEffect(() => {
    const onAvailable = () => setInstallAvailable(true);
    const onInstalled = () => { setInstallState("accepted"); setPlatform("installed"); };
    window.addEventListener("nfcs:install-available", onAvailable);
    window.addEventListener("nfcs:installed", onInstalled);
    return () => {
      window.removeEventListener("nfcs:install-available", onAvailable);
      window.removeEventListener("nfcs:installed", onInstalled);
    };
  }, []);

  // If we're already running standalone AND a role is picked, jump
  // straight to the dashboard. If standalone but NO role picked yet,
  // stay on this page — the role-picker is exactly what we want.
  useEffect(() => {
    if (platform === "installed" && role) {
      navigate(`/${role}`, { replace: true });
    }
  }, [platform, role, navigate]);

  const handleInstall = async () => {
    setInstallState("prompting");
    const outcome = await triggerInstallPrompt();
    if (outcome === "accepted") setInstallState("accepted");
    else if (outcome === "dismissed") setInstallState("dismissed");
    else setInstallState("error");
  };

  const roleLabel = {
    owner: "Owner / Manager",
    staff: "Staff",
    kitchen: "Kitchen",
    bar: "Bar",
    admin: "Admin",
  }[role] || "";

  // Visual palette for the role-picker tiles. Mirrors the audit / user
  // list badges so operators see one consistent color per role.
  const roleTileTone = {
    owner: "border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 text-violet-800 dark:text-violet-200",
    staff: "border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-800 dark:text-emerald-200",
    kitchen: "border-orange-500/40 bg-orange-500/10 hover:bg-orange-500/20 text-orange-800 dark:text-orange-200",
    bar: "border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-800 dark:text-red-200",
  };
  const roleTileIcon = {
    owner: Store,
    staff: ClipboardList,
    kitchen: ChefHat,
    bar: Wine,
  };

  // If the user hasn't picked a role yet, show the role picker first.
  // This is the primary flow when someone opens the business install
  // link (no role in URL) — they pick which device this is.
  const showRolePicker = !role;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-xl px-5 py-10 sm:py-16">
        <div className="mb-8 text-center">
          <img src="/icons/icon-192.png" alt="NFC Solutions" className="mx-auto h-20 w-20 rounded-2xl shadow-lg" />
          <h1 className="mt-4 font-['Space_Grotesk'] text-3xl font-semibold">
            {showRolePicker ? "What is this device?" : "Install NFC Solutions"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {showRolePicker
              ? <>Pick the role this device will be used for{businessCode ? <> at <span className="font-semibold text-foreground">{businessCode}</span></> : null}. We'll remember it so this device always opens the right dashboard.</>
              : <>This device will be set up as the <span className="font-semibold text-foreground">{roleLabel}</span> dashboard{businessCode ? <> for <span className="font-semibold text-foreground">{businessCode}</span></> : null}.</>}
          </p>
        </div>

        {showRolePicker ? (
          <div className="grid grid-cols-2 gap-3">
            {rolesForPicker.map((r) => {
              const Icon = roleTileIcon[r];
              const label = { owner: "Owner / Manager", staff: "Staff", kitchen: "Kitchen", bar: "Bar" }[r];
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setPickedRole(r)}
                  className={`flex flex-col items-center gap-2 rounded-2xl border-2 p-6 text-center transition-colors ${roleTileTone[r]}`}
                  data-testid={`install-role-${r}`}
                >
                  <Icon className="h-8 w-8" strokeWidth={2} />
                  <span className="font-semibold">{label}</span>
                </button>
              );
            })}
          </div>
        ) : platform === "installed" ? (
          <Card className="rounded-[24px] border-emerald-500/40 bg-emerald-500/5">
            <CardContent className="space-y-3 p-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
              <p className="text-lg font-semibold">Already installed</p>
              <p className="text-sm text-muted-foreground">Opening the dashboard…</p>
            </CardContent>
          </Card>
        ) : platform === "ios" ? (
          <Card className="rounded-[24px] border-border bg-card">
            <CardContent className="space-y-4 p-6">
              <p className="text-sm">
                iOS doesn't support one-tap installs from a link, but it takes 5 seconds:
              </p>
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-xs font-bold text-[hsl(var(--accent-foreground))]">1</span>
                  <span>Tap the <span className="font-semibold">Share</span> button in Safari (square with an up arrow at the bottom of the screen).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-xs font-bold text-[hsl(var(--accent-foreground))]">2</span>
                  <span>Scroll down and tap <span className="font-semibold">Add to Home Screen</span>.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-xs font-bold text-[hsl(var(--accent-foreground))]">3</span>
                  <span>Tap <span className="font-semibold">Add</span>. The NFC Solutions icon will appear on your home screen.</span>
                </li>
              </ol>
              <p className="rounded-xl bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                ℹ Important: open this link in <span className="font-semibold">Safari</span>, not Chrome or in-app browsers. iOS only allows home-screen installs from Safari.
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="w-full rounded-xl text-muted-foreground"
                onClick={() => setPickedRole("")}
                data-testid="install-change-role-button"
              >
                Change role
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-[24px] border-border bg-card">
            <CardContent className="space-y-4 p-6 text-center">
              {installAvailable ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Tap the button below and confirm the install prompt. The app will appear on your {platform === "android" ? "home screen" : "applications list"}.
                  </p>
                  <Button
                    size="lg"
                    className="w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                    onClick={handleInstall}
                    disabled={installState === "prompting"}
                    data-testid="install-now-button"
                  >
                    <Download className="h-5 w-5" />
                    {installState === "prompting" ? "Waiting for confirmation…" : "Install Now"}
                  </Button>
                  {installState === "dismissed" && (
                    <p className="text-xs text-muted-foreground">
                      Install cancelled. Tap the button again any time.
                    </p>
                  )}
                  {installState === "error" && (
                    <p className="text-xs text-destructive">
                      Couldn't open the install prompt. Try refreshing the page and tapping again.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm">
                    Your browser hasn't surfaced the install prompt yet. This usually means one of:
                  </p>
                  <ul className="space-y-2 text-left text-sm text-muted-foreground">
                    <li>• The PWA is <span className="font-semibold">already installed</span> on this device — check your home screen / start menu.</li>
                    <li>• Your browser doesn't support PWA installs (try Chrome, Edge, or Samsung Internet).</li>
                    <li>• You need to <span className="font-semibold">interact with the page</span> first — try reloading.</li>
                  </ul>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full rounded-xl"
                    onClick={() => window.location.reload()}
                    data-testid="install-reload-button"
                  >
                    <RefreshCw className="h-5 w-5" />
                    Reload and try again
                  </Button>
                </>
              )}
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-xl text-muted-foreground"
                  onClick={() => setPickedRole("")}
                  data-testid="install-change-role-button"
                >
                  Change role
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-xl text-muted-foreground"
                  onClick={() => navigate(`/${role}${businessCode ? `?business=${encodeURIComponent(businessCode)}` : ""}`)}
                  data-testid="install-skip-button"
                >
                  Skip — open in browser instead
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          NFC Solutions · Secure. Smart. Connected.
        </p>
      </div>
    </div>
  );
}
